import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Lottie from 'lottie-react';
import error from '../../../assets/lottie/error.json';
import {
  HomeIcon,
  ExclamationTriangleIcon,
  ArrowLeftIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

const ModernErrorPage = () => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] relative overflow-hidden flex items-center justify-center">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-72 h-72 bg-gradient-to-r from-red-500/10 to-orange-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-gradient-to-r from-orange-500/10 to-red-500/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-red-500/5 via-orange-500/5 to-yellow-500/5 rounded-full blur-3xl" />
      </div>

      {/* Floating Error Icons */}
      <motion.div
        className="absolute top-20 right-20 w-16 h-16 rounded-full bg-gradient-to-r from-red-500/20 to-orange-500/20 backdrop-blur-sm flex items-center justify-center"
        animate={{
          y: [0, -20, 0],
          rotate: [0, 180, 360]
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      >
        <ExclamationTriangleIcon className="w-8 h-8 text-red-400" />
      </motion.div>

      <motion.div
        className="absolute bottom-32 left-16 w-12 h-12 rounded-full bg-gradient-to-r from-orange-500/20 to-yellow-500/20 backdrop-blur-sm flex items-center justify-center"
        animate={{
          x: [0, 20, 0],
          scale: [1, 1.2, 1]
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      >
        <SparklesIcon className="w-6 h-6 text-orange-400" />
      </motion.div>

      {/* Main Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6">
        <div className="flex flex-col lg:flex-row items-center gap-12">
          
          {/* Left Side - Lottie Animation */}
          <motion.div
            className="w-full lg:w-5/12 flex justify-center order-2 lg:order-1"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <motion.div
              className="w-full max-w-md bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10 shadow-2xl"
              animate={{
                y: [0, -10, 0],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <Lottie 
                animationData={error} 
                className="w-full h-full"
                loop={true}
              />
            </motion.div>
          </motion.div>

          {/* Right Side - Error Content */}
          <motion.div
            className="w-full lg:w-7/12 text-center lg:text-left order-1 lg:order-2"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 md:p-12 border border-white/10 shadow-2xl">
              
              {/* Error Icon */}
              <motion.div
                className="w-16 h-16 mx-auto lg:mx-0 mb-6 rounded-2xl bg-gradient-to-r from-red-500 to-orange-500 p-4 shadow-lg"
                variants={itemVariants}
                whileHover={{ scale: 1.05, rotate: 5 }}
                transition={{ duration: 0.2 }}
              >
                <ExclamationTriangleIcon className="w-full h-full text-white" />
              </motion.div>

              {/* Error Message */}
              <motion.div className="space-y-6" variants={itemVariants}>
                <div>
                  <h1 className="text-4xl md:text-5xl font-black text-white mb-4">
                    <span className="bg-gradient-to-r from-red-400 via-orange-400 to-yellow-400 bg-clip-text text-transparent">
                      Oops!
                    </span>
                    <br />
                    <span className="text-white text-2xl md:text-3xl">Something went wrong</span>
                  </h1>
                  
                  <p className="text-lg text-[#98C1D9]/80 leading-relaxed mb-8">
                    We're sorry, but it looks like the page you're looking for doesn't exist or encountered an unexpected error.
                  </p>
                </div>

                {/* Error Details */}
                <div className="grid grid-cols-1 gap-4">
                  {[
                    "Page not found (404)",
                    "Server temporarily unavailable",
                    "Network connection issue"
                  ].map((detail, index) => (
                    <motion.div
                      key={index}
                      className="flex items-center gap-3 text-left"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
                    >
                      <div className="w-2 h-2 rounded-full bg-gradient-to-r from-red-400 to-orange-400" />
                      <span className="text-[#98C1D9]/80 text-sm">{detail}</span>
                    </motion.div>
                  ))}
                </div>

                {/* Action Buttons */}
                <motion.div 
                  className="flex flex-col sm:flex-row gap-4 pt-6"
                  variants={itemVariants}
                >
                  {/* Back Home Button */}
                  <Link to="/" className="flex-1">
                    <motion.button
                      className="w-full h-12 rounded-lg bg-gradient-to-r from-red-500 to-orange-500 text-white font-bold transition-all duration-300 hover:shadow-lg hover:shadow-red-500/25 flex items-center justify-center gap-2"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <HomeIcon className="w-5 h-5" />
                      Back Home
                    </motion.button>
                  </Link>

                  {/* Go Back Button */}
                  <motion.button
                    onClick={() => window.history.back()}
                    className="flex-1 h-12 rounded-lg bg-white/5 border-2 border-white/20 text-white font-bold hover:bg-white/10 hover:border-white/30 transition-all duration-300 flex items-center justify-center gap-2"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <ArrowLeftIcon className="w-5 h-5" />
                    Go Back
                  </motion.button>
                </motion.div>

                {/* Help Text */}
                <motion.div 
                  className="pt-4 border-t border-white/10"
                  variants={itemVariants}
                >
                  <p className="text-[#98C1D9]/60 text-sm">
                    If the problem persists, please contact our{' '}
                    <a 
                      href="/Home/contact-us" 
                      className="text-orange-400 hover:text-red-400 font-medium transition-colors duration-200"
                    >
                      support team
                    </a>
                    {' '}for assistance.
                  </p>
                </motion.div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ModernErrorPage;